/**
 * API Client for AiPeces Admin Panel
 *
 * KISS: Simplified client focused on admin-panel needs.
 * Same backend as the main app (Azure Functions).
 *
 * Session persistence improvements (aligned with main app):
 * - Proactive token refresh (2 min before expiry)
 * - Retry with refresh on 401 before logout
 * - Storage sync across tabs
 */

// ─── URL Configuration ─────────────────────────────────────────────────────

const PRODUCTION_API_URL = 'https://aipeces-pro-functions.azurewebsites.net/api';

function resolveApiBase(): string {
    const env = import.meta.env.VITE_API_URL;
    if (env) {
        let base = env.replace(/\/+$/, '');
        if (base.includes('azurewebsites.net') && !base.endsWith('/api')) {
            base = `${base}/api`;
        }
        return base;
    }
    if (import.meta.env.DEV) return '/api';
    return PRODUCTION_API_URL;
}

/** Base URL including /api path — ready for endpoint concatenation */
export const API_BASE_URL = resolveApiBase();

// ─── Session Storage ────────────────────────────────────────────────────────

const SESSION_KEY = 'admin_session';

/** 2 minutes — refresh proactively before token expires (like main app tokenManager) */
const TOKEN_REFRESH_BUFFER_MS = 120_000;

/** Evita “Verificando acceso…” / “Subiendo…” eternos si auth o red no responden. */
const DEFAULT_FETCH_TIMEOUT_MS = 20_000;
const AUTH_FETCH_TIMEOUT_MS = 12_000;

function fetchWithTimeout(
    url: string,
    init: RequestInit = {},
    timeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const parent = init.signal;
    const onParentAbort = () => controller.abort();
    if (parent) {
        if (parent.aborted) controller.abort();
        else parent.addEventListener('abort', onParentAbort, { once: true });
    }
    return fetch(url, { ...init, signal: controller.signal }).finally(() => {
        clearTimeout(timer);
        if (parent) parent.removeEventListener('abort', onParentAbort);
    });
}

export interface StoredSession {
    access_token: string;
    refresh_token?: string;
    expires_at?: number;
    user?: {
        id: string;
        email: string;
        display_name?: string;
        avatar_url?: string;
    };
}

function getSession(): StoredSession | null {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function saveSession(session: StoredSession): void {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession(): void {
    localStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new CustomEvent('auth:session-cleared'));
}

// Sync session across tabs: when another tab clears session, this tab should logout too
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key === SESSION_KEY && e.newValue === null) {
            window.dispatchEvent(new CustomEvent('auth:session-cleared'));
        }
    });
}

// ─── Token Validation ───────────────────────────────────────────────────────

function isTokenExpired(token: string, bufferMs = TOKEN_REFRESH_BUFFER_MS): boolean {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return Date.now() >= (payload.exp * 1000) - bufferMs;
    } catch { return true; }
}

/** Try to refresh session; returns new access_token or null on failure */
async function tryRefresh(session: StoredSession): Promise<string | null> {
    if (!session.refresh_token) return null;
    try {
        const res = await fetchWithTimeout(
            `${API_BASE_URL}/auth-refresh`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: session.refresh_token }),
            },
            AUTH_FETCH_TIMEOUT_MS,
        );
        if (!res.ok) return null;
        const data = await res.json();
        const newAccessToken = data.access_token || data.accessToken;
        if (!newAccessToken) return null;
        const expiresIn = data.expires_in ?? data.expiresIn ?? 3600;
        saveSession({
            ...session,
            access_token: newAccessToken,
            refresh_token: data.refresh_token || data.refreshToken || session.refresh_token,
            expires_at: Date.now() + expiresIn * 1000,
        });
        return newAccessToken;
    } catch {
        return null;
    }
}

async function getValidToken(): Promise<string | null> {
    const session = getSession();
    if (!session?.access_token) return null;

    if (!isTokenExpired(session.access_token)) {
        return session.access_token;
    }

    const refreshed = await tryRefresh(session);
    return refreshed ?? session.access_token;
}

// ─── HTTP Engine ────────────────────────────────────────────────────────────

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    body?: unknown;
    headers?: Record<string, string>;
    requiresAuth?: boolean;
}

export interface ApiResult<T = unknown> {
    data?: T;
    error?: { message: string; code?: string; status?: number };
}

async function request<T = unknown>(endpoint: string, opts: RequestOptions = {}, isRetry = false): Promise<ApiResult<T>> {
    const { method = 'GET', body, headers = {}, requiresAuth = true } = opts;
    const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

    const reqHeaders: Record<string, string> = { 'Content-Type': 'application/json', ...headers };

    if (requiresAuth) {
        const token = await getValidToken();
        if (token) reqHeaders['Authorization'] = `Bearer ${token}`;
    }

    const isAuthEndpoint = /\/auth-(me|refresh|google)/.test(endpoint);
    const timeoutMs = isAuthEndpoint ? AUTH_FETCH_TIMEOUT_MS : DEFAULT_FETCH_TIMEOUT_MS;

    try {
        const res = await fetchWithTimeout(
            url,
            {
                method,
                headers: reqHeaders,
                body: body ? JSON.stringify(body) : undefined,
            },
            timeoutMs,
        );

        const contentType = res.headers.get('content-type');
        const data = contentType?.includes('json') ? await res.json() : await res.text().catch(() => '');

        // 401: try refresh once before logout (avoids kicking user out on transient failures)
        if (res.status === 401 && requiresAuth && !isRetry) {
            const session = getSession();
            const refreshed = session ? await tryRefresh(session) : null;
            if (refreshed) {
                return request<T>(endpoint, opts, true);
            }
            clearSession();
            return { error: { message: 'Sesión expirada', code: 'AUTH_EXPIRED', status: 401 } };
        }

        if (res.status === 401 && requiresAuth && isRetry) {
            clearSession();
            return { error: { message: 'Sesión expirada', code: 'AUTH_EXPIRED', status: 401 } };
        }

        if (!res.ok) {
            return { error: { message: data?.error || data?.message || `HTTP ${res.status}`, status: res.status } };
        }

        return { data };
    } catch (err) {
        const aborted = err instanceof DOMException && err.name === 'AbortError';
        return {
            error: {
                message: aborted
                    ? 'La petición ha tardado demasiado. Vuelve a iniciar sesión si el Portal no responde.'
                    : err instanceof Error ? err.message : 'Error de red',
                code: aborted ? 'TIMEOUT' : 'NETWORK',
            },
        };
    }
}

// ─── Public API (KISS — only methods the admin panel actually uses) ──────────

export const apiClient = {
    // Session
    getSession,
    saveSession,
    clearSession,
    getValidToken,

    /** API base URL (includes /api) */
    baseUrl: API_BASE_URL,

    // HTTP
    get: <T = unknown>(endpoint: string) => request<T>(endpoint),
    post: <T = unknown>(endpoint: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
        request<T>(endpoint, { ...opts, method: 'POST', body }),
    put: <T = unknown>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PUT', body }),
    delete: <T = unknown>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),

    /**
     * Call an RPC function. This is the primary data access pattern for the admin panel.
     * Uses the same endpoint pattern as the main app: POST /api/rpc/{functionName}
     * Params are sent directly as the request body.
     */
    rpc: async <T = unknown>(functionName: string, params?: Record<string, unknown>): Promise<ApiResult<T>> => {
        return request<T>(`/rpc/${functionName}`, { method: 'POST', body: params || {} });
    },

    /**
     * Upload a file to Azure Blob Storage via the storage-upload function.
     * Converts the file to base64 and sends it as JSON.
     * @param bucket - The storage bucket (e.g., 'material-images')
     * @param path - File path within the bucket (e.g., 'my-material/photo.jpg')
     * @param file - The File object to upload
     * @returns The public blob URL
     */
    uploadFile: async (bucket: string, path: string, file: File): Promise<ApiResult<{ path: string; url: string; publicUrl: string }>> => {
        // Convert file to base64
        const buffer = await file.arrayBuffer();
        const base64 = btoa(
            new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        return request(`/storage/${bucket}/upload`, {
            method: 'POST',
            body: {
                data: base64,
                path,
                contentType: file.type || 'image/jpeg',
                upsert: true,
            },
        });
    },

    /**
     * Subida directa a Blob vía SAS (storage-generate-upload-url).
     * Obligatoria para MP4 grandes (~60 MB); no usar uploadFile/base64.
     */
    uploadViaSas: async (
        bucket: string,
        path: string,
        file: File,
        opts?: {
            contentType?: string;
            cacheControl?: string;
            onProgress?: (pct: number) => void;
        },
    ): Promise<ApiResult<{ path: string; url: string; publicUrl: string }>> => {
        const contentType = opts?.contentType || file.type || 'application/octet-stream';
        const cacheControl = opts?.cacheControl || 'public, max-age=31536000, immutable';

        const sas = await request<{
            uploadUrl: string;
            blobPath: string;
            blobUrl: string;
            contentType?: string;
        }>(`/storage/${bucket}/generate-upload-url`, {
            method: 'POST',
            body: { path, contentType, cacheControl },
        });
        if (sas.error || !sas.data?.uploadUrl) {
            return { error: sas.error || { message: 'No se pudo generar URL de subida' } };
        }

        try {
            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', sas.data!.uploadUrl, true);
                xhr.timeout = 10 * 60 * 1000;
                xhr.setRequestHeader('Content-Type', contentType);
                xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
                xhr.setRequestHeader('x-ms-blob-content-type', contentType);
                xhr.setRequestHeader('x-ms-blob-cache-control', cacheControl);
                xhr.upload.onprogress = (ev) => {
                    if (ev.lengthComputable && opts?.onProgress) {
                        opts.onProgress(Math.round((ev.loaded / ev.total) * 100));
                    }
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else reject(new Error(`Subida Blob falló HTTP ${xhr.status}`));
                };
                xhr.onerror = () => reject(new Error('Error de red al subir al Blob'));
                xhr.ontimeout = () => reject(new Error('La subida ha excedido el tiempo máximo'));
                xhr.send(file);
            });

            const url = sas.data.blobUrl;
            return {
                data: {
                    path: sas.data.blobPath,
                    url,
                    publicUrl: url,
                },
            };
        } catch (err) {
            return {
                error: {
                    message: err instanceof Error ? err.message : 'Error al subir archivo',
                },
            };
        }
    },
};
